import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrganizationSettings() {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchTenantData();
  }, []);

  const fetchTenantData = async () => {
    try {
      const response = await axios.get(`${API_URL}/tenant`);
      setTenant(response.data);
      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching tenant:', error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato non supportato. Usa JPG, PNG, WEBP o GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 5MB');
      return;
    }

    setUploading(true);

    try {
      // Upload image
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const logoUrl = `${process.env.REACT_APP_BACKEND_URL}${uploadResponse.data.path}`;

      // Update tenant with logo URL
      await axios.patch(`${API_URL}/tenant/logo`, {
        logo_url: logoUrl
      });

      setLogoPreview(logoUrl);
      toast.success('Logo caricato con successo!');
      
      // Refresh tenant data
      fetchTenantData();
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Errore durante il caricamento del logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Logo Organizzazione
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Questo logo apparirà:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Nelle anteprime WhatsApp degli eventi</li>
                  <li>Nei biglietti PDF generati</li>
                  <li>Nelle email inviate agli ospiti</li>
                  <li>Nei report degli eventi</li>
                </ul>
                <p className="mt-2 text-xs">
                  <strong>Dimensioni consigliate:</strong> 1200x630px (formato 1.91:1) per WhatsApp/Social Media
                </p>
              </div>
            </div>

            {/* Logo Preview */}
            {logoPreview && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
                <div className="flex flex-col items-center">
                  <p className="text-sm text-gray-600 mb-3">Logo Attuale:</p>
                  <img 
                    src={logoPreview} 
                    alt="Logo organizzazione" 
                    className="max-w-sm max-h-40 object-contain rounded shadow-md"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x200?text=Logo';
                    }}
                  />
                </div>
              </div>
            )}

            {/* Upload Button */}
            <div>
              <Label htmlFor="logo-upload" className="text-sm font-medium mb-2 block">
                {logoPreview ? 'Cambia Logo' : 'Carica Logo'}
              </Label>
              <div className="flex items-center gap-3">
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button
                  type="button"
                  onClick={() => document.getElementById('logo-upload').click()}
                  disabled={uploading}
                  className="gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Caricamento...' : logoPreview ? 'Cambia Logo' : 'Carica Logo'}
                </Button>
                <span className="text-xs text-gray-500">
                  JPG, PNG, WEBP o GIF (max 5MB)
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Organization Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informazioni Organizzazione</CardTitle>
        </CardHeader>
        <CardContent>
          {tenant ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Nome</p>
                <p className="font-medium">{tenant.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Piano</p>
                <p className="font-medium capitalize">{tenant.subscription_plan || 'basic'}</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Caricamento...</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
